import React, { memo, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Map as ImmutableMap } from 'immutable';
import { stringSimilarity } from 'string-similarity-js';

import config from '../../config';
import Button from '../helpers/Button';
import DropDown, { DropDownElement } from '../helpers/DropDown';
import MailingInfoTable from './MailingInfoTable';
import Select from '../helpers/Select';
import { AppPage } from '../AppPage';
import { GridCard } from '../helpers/GridCard';
import { MailingSummary } from '../MailingSummary';
import { AttachmentRenderer } from './AttachmentRenderer';

import style from './style.module.css';

const columnDefs = config.mailingInfo.getIn(['table', 'columnDefs']);
const NAME_ATTACHMENT_FOLDER = 'attachmentFolder';
const SIMILARITY = 0.5;
const attachedFiles = new Map();

const addFile = (file, id, targetId) => {
  const existingEntry = attachedFiles.get(id);

  if (existingEntry) {
    const alreadyExists = existingEntry.files.some(f => f.name === file.name);

    if (!alreadyExists) {
      existingEntry.files.push(file);
    }
  } else {
    attachedFiles.set(id, {
      targetId,
      files: [file],
    });
  }
};

const removeFile = (targetId, fileName) => {
  const entry = attachedFiles.get(targetId);

  if (!entry) return;

  entry.files = entry.files.filter(f => f.name !== fileName);

  if (entry.files.length === 0) {
    attachedFiles.delete(targetId);
  }
};

const removeAllFiles = () => {
  attachedFiles.clear();
};

const fileNamesById = id => attachedFiles.get(id)?.files.map(file => file.name) || [];

/**
 * Mailing page component.
 *
 * @param props {Object}.
 */
const MailingInfoComponent = ({
  mailingInfo,
  values,
  emailTemplateId,
  onPreviewEmailTemplate,
  onUpdateEmailAttachment,
  onChange,
  onChangeEmailTemplate,
  onGetNextPageData,
  onDoubleClickRow,
  onReload,
  onMarkCompleted,
  onSendEmails,
  onSetNextActions,
  onUpdateCell,
  onCancelMailing,
  ...rest
}) => {
  const inputFileSelectRef = useRef(null);

  const footerDropDown = values.get('footerDropDown').map(element => {
    const attributes = element.get('attributes').toJS();
    const name = element.get('name');

    return (
      <DropDownElement {...attributes} key={name} onClick={rest[element.get('onClickField')]}>
        {name}
      </DropDownElement>
    );
  });

  const isSubmitted = mailingInfo.getIn(['header', 'date'], true);
  const isDisabledNextAction = !mailingInfo.get('setNextAction', false);
  const isAvailablePreview = emailTemplateId && mailingInfo.get('queryResults').size > 0;

  const processedColumnDefs = useMemo(
    () =>
      columnDefs.map(columnDef => {
        const field = columnDef.get('field');

        switch (field) {
          case 'attachment':
            return columnDef.set('cellRendererFramework', params => (
              <AttachmentRenderer
                onDelete={handleFileDelete}
                onSelect={file => handleFileSelect(file, params.data)}
                target={params.data}
              />
            ));

          default:
            return columnDef;
        }
      }),
    [],
  );

  const handleOpenFileModal = () => {
    if (inputFileSelectRef?.current) {
      inputFileSelectRef.current.value = '';
      inputFileSelectRef.current.click();
    }
  };

  const handleFolderSelect = event => {
    if (event.currentTarget.files) {
      removeAllFiles();

      const { files } = event.currentTarget;

      Array.from(files).forEach(file => {
        const nameWithoutExt = file.name
          .split('.')
          .slice(0, -1)
          .join('.');
        const queryResults = mailingInfo.get('queryResults');

        queryResults.forEach(queryResult => {
          if (stringSimilarity(queryResult.targetAbbrName, nameWithoutExt) >= SIMILARITY) {
            onUpdateEmailAttachment(queryResult.id, [file.name]);
            addFile(file, queryResult.id, queryResult.targetId);
          }
        });
      });
    }
  };

  const handleFileSelect = (file, data) => {
    addFile(file, data.id, data.targetId);

    onUpdateEmailAttachment(data.id, fileNamesById(data.id));
  };

  const handleFileDelete = (id, fileName) => {
    removeFile(id, fileName);

    onUpdateEmailAttachment(id, fileNamesById(id));
  };

  const handleSendEmails = () => {
    const files = Array.from(attachedFiles.values());

    onSendEmails(files, removeAllFiles);
  };

  const toolbar = (
    <div className={style.toolbar}>
      <Button onClick={handleOpenFileModal}>
        <i aria-hidden="true" className="fa fa-folder-open-o" /> Select Attachment Folder
      </Button>
      <input
        ref={inputFileSelectRef}
        id={NAME_ATTACHMENT_FOLDER}
        name={NAME_ATTACHMENT_FOLDER}
        onChange={handleFolderSelect}
        type="file"
        webkitdirectory=""
        hidden
        multiple
      />
      <DropDown buttonContent="Download" className="btn-default btn-xs" id="dropdownMenu7">
        {footerDropDown}
      </DropDown>
      <Button disabled={isSubmitted} onClick={onMarkCompleted}>
        Mark Completed
      </Button>
      <Button disabled={isSubmitted} onClick={onCancelMailing}>
        Cancel Mailing
      </Button>
      <Button disabled={isDisabledNextAction} onClick={onSetNextActions}>
        Set Next Actions
      </Button>
      <Button onClick={() => onReload()}>
        <i aria-hidden="true" className="fa fa-refresh" /> Reload
      </Button>
      {emailTemplateId && (
        <Button className="btn-primary" disabled={mailingInfo.get('isSendingEmails')} onClick={handleSendEmails}>
          <i aria-hidden="true" className="fa fa-paper-plane-o" /> Send
        </Button>
      )}
    </div>
  );

  return (
    <AppPage overline="Mailing" title="Mailing" toolbar={toolbar}>
      <MailingSummary
        header={mailingInfo.get('header')}
        isEmail={mailingInfo.getIn(['header', 'isemail']) === true}
        showPm
      >
        <div className={style.template}>
          <Select
            defaultText="Select A Template"
            defaultValue=""
            label="Select Template"
            nameKey="templateName"
            onChange={onChangeEmailTemplate}
            options={mailingInfo.get('emailTemplates')}
            valueKey="id"
            disableDefault
            showLabel
          />
          {isAvailablePreview && (
            <Button onClick={onPreviewEmailTemplate}>
              <i aria-hidden="true" className="fa fa-search" /> Email Preview
            </Button>
          )}
        </div>
      </MailingSummary>
      <GridCard className={style.grid} dense>
        <MailingInfoTable
          columnDefs={processedColumnDefs}
          data={mailingInfo}
          onChange={onChange}
          onDoubleClickRow={onDoubleClickRow}
          onGetNextPageData={onGetNextPageData}
          onUpdateCell={onUpdateCell}
        />
      </GridCard>
    </AppPage>
  );
};

MailingInfoComponent.propTypes = {
  mailingInfo: PropTypes.instanceOf(ImmutableMap).isRequired,
  onChange: PropTypes.func.isRequired,
  onDoubleClickRow: PropTypes.func.isRequired,
  onGetNextPageData: PropTypes.func.isRequired,
  onMarkCompleted: PropTypes.func.isRequired,
  onReload: PropTypes.func.isRequired,
  onSendEmails: PropTypes.func.isRequired,
  onSetNextActions: PropTypes.func.isRequired,
  onUpdateCell: PropTypes.func.isRequired,
  onUpdateEmailAttachment: PropTypes.func.isRequired,
  values: PropTypes.instanceOf(ImmutableMap).isRequired,
};

export const MailingInfo = memo(MailingInfoComponent);
